// Icons
import { ChevronRightIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/chevron-right-icon';
import { GripVerticalIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/grip-vertical-icon';
import { Repeat2Icon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/repeat-2-icon';

// Tiptap UI
import { TableAlignMenu } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/ui/table-alignment-menu';
import { useTableBorderToggle } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/ui/table-border-toggle-button';
import { useTableClearRowColumnContent } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/ui/table-clear-row-column-content-button';
import { useTableFitToWidth } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/ui/table-fit-to-width-button';

// Primitive UI Components
import { Button, ButtonGroup } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';
import {
    Combobox,
    ComboboxList,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/combobox';
import {
    Menu,
    MenuButton,
    MenuContent,
    MenuGroup,
    MenuGroupLabel,
    MenuItem,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/menu';
import { Separator } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/separator';
import { Spacer } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/spacer';
import {
    AskAiShortcutBadge,
    useAiAsk,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/ai-ask-button';
import { useBlockquote } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/blockquote-button';
import { useCodeBlock } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/code-block-button';
import { ColorMenu } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/color-menu';
import {
    CopyAnchorLinkShortcutBadge,
    useCopyAnchorLink,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/copy-anchor-link-button';
import {
    CopyToClipboardShortcutBadge,
    useCopyToClipboard,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/copy-to-clipboard-button';
import {
    DeleteNodeShortcutBadge,
    useDeleteNode,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/delete-node-button';
import type {
    DragContextMenuProps,
    MenuItemProps,
    NodeChangeData,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/drag-context-menu/drag-context-menu-types';
import {
    DuplicateShortcutBadge,
    useDuplicate,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/duplicate-button';
import { useHeading } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/heading-button';
import { useImageDownload } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/image-download-button';
import { useList } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/list-button';
import { useResetAllFormatting } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/reset-all-formatting-button';
import { SlashCommandTriggerButton } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/slash-command-trigger-button';
import { useText } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/text-button';

// Hooks
import { selectNodeAndHideFloating } from '@examdojo/core/tiptap-react-ui-components/hooks/use-floating-toolbar-visibility';
import { useIsBreakpoint } from '@examdojo/core/tiptap-react-ui-components/hooks/use-is-breakpoint';
import { useTiptapEditor } from '@examdojo/core/tiptap-react-ui-components/hooks/use-tiptap-editor';
import { useUiEditorState } from '@examdojo/core/tiptap-react-ui-components/hooks/use-ui-editor-state';

// Utils
import {
    getNodeDisplayName,
    isTextSelectionValid,
} from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-collab-utils';
import { SR_ONLY } from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-utils';
import { offset } from '@floating-ui/react';
import { DragHandle } from '@tiptap/extension-drag-handle-react';
import type { Node as TiptapNode } from '@tiptap/pm/model';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ACCORDION_DRAG_RULES } from './accordion-drag-rules';
import { DETAILS_DRAG_RULES } from './details-drag-rules';
import { LIST_FRIENDLY_DEFAULT_RULES } from './list-drag-rules';
import { TABLE_DRAG_RULES } from './table-drag-rules';

const useNodeTransformActions = () => {
  const text = useText();
  const heading1 = useHeading({ level: 1 });
  const heading2 = useHeading({ level: 2 });
  const heading3 = useHeading({ level: 3 });
  const bulletList = useList({ type: 'bulletList' });
  const orderedList = useList({ type: 'orderedList' });
  const taskList = useList({ type: 'taskList' });
  const blockquote = useBlockquote();
  const codeBlock = useCodeBlock();

  const mapper = (
    action: ReturnType<
      typeof useText | typeof useHeading | typeof useList | typeof useBlockquote | typeof useCodeBlock
    >,
  ) => ({
    icon: action.Icon,
    label: action.label,
    onClick: action.handleToggle,
    disabled: !action.canToggle,
    isActive: action.isActive,
  });

  const actions = [
    mapper(text),
    ...[heading1, heading2, heading3].map(mapper),
    mapper(bulletList),
    mapper(orderedList),
    mapper(taskList),
    mapper(blockquote),
    mapper(codeBlock),
  ];

  const allDisabled = actions.every((a) => a.disabled);

  return allDisabled ? null : actions;
};

const BaseMenuItem: React.FC<MenuItemProps> = ({
  icon: Icon,
  label,
  onClick,
  disabled = false,
  isActive = false,
  shortcutBadge,
}) => (
  <MenuItem
    render={<Button data-style="ghost" data-active-state={isActive ? 'on' : 'off'} />}
    onClick={onClick}
    disabled={disabled}
  >
    <Icon className="tiptap-button-icon" />
    <span className="tiptap-button-text">{label}</span>
    {shortcutBadge}
  </MenuItem>
);

const SubMenuTrigger: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}> = ({ icon: Icon, label, children }) => (
  <Menu
    placement="right"
    trigger={
      <MenuItem
        render={
          <MenuButton
            render={
              <Button data-style="ghost">
                <Icon className="tiptap-button-icon" />
                <span className="tiptap-button-text">{label}</span>
                <Spacer />
                <ChevronRightIcon className="tiptap-button-icon" />
              </Button>
            }
          />
        }
      />
    }
  >
    <MenuContent portal>
      <ComboboxList>{children}</ComboboxList>
    </MenuContent>
  </Menu>
);

const TransformActionGroup: React.FC = () => {
  const actions = useNodeTransformActions();
  const { canReset, handleResetFormatting, label, Icon } = useResetAllFormatting({
    hideWhenUnavailable: true,
    preserveMarks: ['inlineThread'],
  });

  if (!actions && !canReset) return null;

  return (
    <>
      {actions && (
        <SubMenuTrigger icon={Repeat2Icon} label="Turn Into">
          <MenuGroup>
            <MenuGroupLabel>Turn into</MenuGroupLabel>
            {actions.map((action) => (
              <BaseMenuItem key={action.label} {...action} />
            ))}
          </MenuGroup>
        </SubMenuTrigger>
      )}

      {canReset && <BaseMenuItem icon={Icon} label={label} disabled={!canReset} onClick={handleResetFormatting} />}

      <Separator orientation="horizontal" />
    </>
  );
};

const TableFitToWidth: React.FC = () => {
  const { canFitToWidth, handleFitToWidth, label, Icon } = useTableFitToWidth({
    hideWhenUnavailable: true,
  });
  const {
    canToggle,
    handleToggle,
    label: borderLabel,
    Icon: BorderIcon,
  } = useTableBorderToggle({
    hideWhenUnavailable: true,
  });
  const clearAllContents = useTableClearRowColumnContent({ resetAttrs: true });

  return (
    <>
      {canFitToWidth && <BaseMenuItem icon={Icon} label={label} disabled={!canFitToWidth} onClick={handleFitToWidth} />}

      {canToggle && <BaseMenuItem icon={BorderIcon} label={borderLabel} disabled={!canToggle} onClick={handleToggle} />}

      {clearAllContents.canClearRowColumnContent && (
        <BaseMenuItem
          icon={clearAllContents.Icon}
          label={'Clear all contents'}
          disabled={!clearAllContents.canClearRowColumnContent}
          onClick={clearAllContents.handleClear}
        />
      )}
    </>
  );
};

const ImageActionGroup: React.FC = () => {
  const { canDownload, handleDownload, label, Icon } = useImageDownload({
    hideWhenUnavailable: true,
  });

  if (!canDownload) return null;

  return (
    <>
      <BaseMenuItem icon={Icon} label={label} disabled={!canDownload} onClick={handleDownload} />

      <Separator orientation="horizontal" />
    </>
  );
};

const CoreActionGroup: React.FC = () => {
  const { handleDuplicate, canDuplicate, label, Icon: DuplicateIcon } = useDuplicate();
  const { handleCopyToClipboard, canCopyToClipboard, label: copyLabel, Icon: CopyIcon } = useCopyToClipboard();
  const {
    handleCopyAnchorLink,
    canCopyAnchorLink,
    label: copyAnchorLinkLabel,
    Icon: CopyAnchorLinkIcon,
  } = useCopyAnchorLink();

  return (
    <>
      <MenuGroup>
        <BaseMenuItem
          icon={DuplicateIcon}
          label={label}
          onClick={handleDuplicate}
          disabled={!canDuplicate}
          shortcutBadge={<DuplicateShortcutBadge />}
        />
        <BaseMenuItem
          icon={CopyIcon}
          label={copyLabel}
          onClick={handleCopyToClipboard}
          disabled={!canCopyToClipboard}
          shortcutBadge={<CopyToClipboardShortcutBadge />}
        />
        <BaseMenuItem
          icon={CopyAnchorLinkIcon}
          label={copyAnchorLinkLabel}
          onClick={handleCopyAnchorLink}
          disabled={!canCopyAnchorLink}
          shortcutBadge={<CopyAnchorLinkShortcutBadge />}
        />
      </MenuGroup>

      <Separator orientation="horizontal" />
    </>
  );
};

const AIActionGroup: React.FC = () => {
  const { handleAiAsk, canAiAsk, Icon: AiAskIcon } = useAiAsk();

  if (!canAiAsk) return null;

  return (
    <>
      <MenuGroup>
        {canAiAsk && (
          <BaseMenuItem icon={AiAskIcon} label="Ask AI" onClick={handleAiAsk} shortcutBadge={<AskAiShortcutBadge />} />
        )}
      </MenuGroup>

      <Separator orientation="horizontal" />
    </>
  );
};

const DeleteActionGroup: React.FC = () => {
  const { handleDeleteNode, canDeleteNode, label, Icon } = useDeleteNode();

  return (
    <MenuGroup>
      <BaseMenuItem
        icon={Icon}
        label={label}
        onClick={handleDeleteNode}
        disabled={!canDeleteNode}
        shortcutBadge={<DeleteNodeShortcutBadge />}
      />
    </MenuGroup>
  );
};

export const DragContextMenu: React.FC<DragContextMenuProps> = ({
  editor: providedEditor,
  withSlashCommandTrigger = true,
  mobileBreakpoint = 768,
  ...props
}) => {
  const { editor } = useTiptapEditor(providedEditor);
  const { aiGenerationActive, isDragging } = useUiEditorState(editor);
  const isMobile = useIsBreakpoint('max', mobileBreakpoint);
  const [open, setOpen] = useState(false);
  const [node, setNode] = useState<TiptapNode | null>(null);
  const [nodePos, setNodePos] = useState<number>(-1);
  const nodeTypeRef = useRef<string | null>(null);

  const handleNodeChange = useCallback((data: NodeChangeData) => {
    nodeTypeRef.current = data.node?.type.name ?? null;
    // flushSync forces React to commit the state update synchronously,
    // so the DragHandle element has correct dimensions when the plugin
    // calls computePosition immediately after this callback.
    flushSync(() => {
      if (data.node) setNode(data.node);
      setNodePos(data.pos);
    });
  }, []);

  useEffect(() => {
    if (!editor) return;
    editor.commands.setLockDragHandle(open);
    editor.commands.setMeta('lockDragHandle', open);
  }, [editor, open]);

  const dynamicPositions = useMemo(() => {
    // Height threshold above which a node is considered a tall block (e.g. image, code block) and the drag handle aligns to the top instead of centering.
    const DEFAULT_BLOCK_HEIGHT_THRESHOLD = 40;

    return {
      middleware: [
        offset((props) => {
          const { rects } = props;
          const nodeHeight = rects.reference.height;
          const dragHandleHeight = rects.floating.height;

          let crossAxis = nodeHeight > DEFAULT_BLOCK_HEIGHT_THRESHOLD ? 0 : nodeHeight / 2 - dragHandleHeight / 2;

          // For details (toggle) nodes, always center relative to the summary area (~first line), regardless of expanded content height.
          if (nodeTypeRef.current === 'details') {
            const effectiveHeight = Math.min(nodeHeight, 32);
            crossAxis = effectiveHeight / 2 - dragHandleHeight / 2;
          }

          // For accordion nodes, center relative to the "Add panel" button at the top (~36px).
          if (nodeTypeRef.current === 'accordion') {
            const effectiveHeight = Math.min(nodeHeight, 36);
            crossAxis = effectiveHeight / 2 - dragHandleHeight / 2;
          }

          return {
            mainAxis: 0,
            crossAxis,
          };
        }),
      ],
    };
  }, []);

  const handleOnMenuClose = useCallback(() => {
    if (editor) {
      editor.commands.setMeta('hideDragHandle', true);
    }
  }, [editor]);

  const onElementDragStart = useCallback(() => {
    if (!editor) return;
    editor.commands.setIsDragging(true);
  }, [editor]);

  const onElementDragEnd = useCallback(() => {
    if (!editor) return;
    editor.commands.setIsDragging(false);

    setTimeout(() => {
      editor.view.dom.blur();
      editor.view.focus();
    }, 0);
  }, [editor]);

  if (!editor) return null;

  const nodeName = getNodeDisplayName(editor);
  const isTopLevelNode = nodePos >= 0 && editor.state.doc.resolve(nodePos).depth <= 1;

  return (
    <DragHandle
      editor={editor}
      nested={{
        defaultRules: false,
        edgeDetection: {
          threshold: 0,
        },
        rules: [...LIST_FRIENDLY_DEFAULT_RULES, ...DETAILS_DRAG_RULES, ...ACCORDION_DRAG_RULES, ...TABLE_DRAG_RULES],
      }}
      onNodeChange={handleNodeChange}
      computePositionConfig={dynamicPositions}
      onElementDragStart={onElementDragStart}
      onElementDragEnd={onElementDragEnd}
      {...props}
    >
      <ButtonGroup
        orientation="horizontal"
        style={{
          ...(aiGenerationActive || isMobile || isTextSelectionValid(editor)
            ? { opacity: 0, pointerEvents: 'none' }
            : {}),
          ...(isDragging ? { opacity: 0 } : {}),
        }}
      >
        {withSlashCommandTrigger && isTopLevelNode && (
          <SlashCommandTriggerButton node={node} nodePos={nodePos} data-weight="small" />
        )}

        <Menu
          open={open}
          onOpenChange={setOpen}
          placement="left"
          trigger={
            <MenuButton
              render={
                <Button
                  data-style="ghost"
                  tabIndex={-1}
                  tooltip={
                    <>
                      <div>Click for options</div>
                      <div>Hold for drag</div>
                    </>
                  }
                  data-weight="small"
                  style={{
                    cursor: 'grab',
                    ...(open ? { pointerEvents: 'none' } : {}),
                  }}
                  onMouseDown={() => selectNodeAndHideFloating(editor, nodePos)}
                >
                  <GripVerticalIcon className="tiptap-button-icon" />
                </Button>
              }
            />
          }
        >
          <MenuContent onClose={handleOnMenuClose} autoFocusOnHide={false} preventBodyScroll={true} portal>
            <Combobox style={SR_ONLY} />
            <ComboboxList style={{ minWidth: '15rem' }}>
              <MenuGroup>
                <MenuGroupLabel>{nodeName}</MenuGroupLabel>
                <ColorMenu />
                <TableAlignMenu />
                <TableFitToWidth />
                <TransformActionGroup />
                <ImageActionGroup />
              </MenuGroup>

              <CoreActionGroup />

              <AIActionGroup />

              <DeleteActionGroup />
            </ComboboxList>
          </MenuContent>
        </Menu>
      </ButtonGroup>
    </DragHandle>
  );
};
